import React, { useState } from 'react';
import { useTranslations } from 'use-intl';
import { Typography } from '@/UIlibrary/Typography/Typography';
import InputField from '@/UIlibrary/InputField/InputField';
import { useSupabaseStore } from '../supabaseStore';
import { useChatStore } from '../useChat';
import ConfirmModal from '@/stories/Components/ConfirmModal/ConfirmModal';

interface UpdateCustomerNameModalProps {
	isOpen: boolean;
	onClose: () => void;
}

const UpdateCustomerNameModal: React.FC<UpdateCustomerNameModalProps> = ({ isOpen, onClose }) => {
	const t = useTranslations('Support');
	const supabase = useSupabaseStore((state) => state.supabase);
	const { selectedChat } = useChatStore();
	const [customerName, setCustomerName] = useState(selectedChat?.customer_name || '');
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState('');

	const handleUpdateCustomerName = async () => {
		if (!supabase || !selectedChat?.id) return;
		setIsLoading(true);
		setError('');
		try {
			const { error: updateError } = await supabase
				.from('chats')
				.update({ customer_name: customerName })
				.eq('id', selectedChat.id);
			if (updateError) throw updateError;
			handleClose();
		} catch (err) {
			console.error('Error updating customer name:', err);
			setError(t('ErrorUpdatingCustomerName') || 'Error updating customer name');
		} finally {
			setIsLoading(false);
		}
	};

	const handleClose = () => {
		onClose();
		setError('');
		setCustomerName(selectedChat?.customer_name || '');
	};

	const customerNameInputContent = (
		<div className="space-y-4">
			<div className="space-y-2">
				<InputField
					label={t('CustomerName')}
					name="customerName"
					value={customerName}
					onChange={(e) => setCustomerName(e.target.value)}
					placeholder={t('enterCustomerName') || 'Enter customer name'}
				/>
			</div>
			{error && (
				<Typography color="error" size="sm">
					{error}
				</Typography>
			)}
		</div>
	);

	return (
		<ConfirmModal
			isOpen={isOpen}
			onClose={handleClose}
			title={t('UpdateCustomerName')}
			subtitle={t('UpdateCustomerNameDescription')}
			confirmActionText={isLoading ? t('Updating') : t('Update')}
			onConfirm={handleUpdateCustomerName}
			onCancel={handleClose}
			confirmBtnLoading={isLoading}
			confirmBtnTestId="updateCustomerNameBtn"
			cancelBtnTestId="cancelCustomerNameBtn"
			type="confirm"
			maxWidth={500}
		>
			{customerNameInputContent}
		</ConfirmModal>
	);
};

export default UpdateCustomerNameModal;
